import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { FormControlData } from './form-control-data';
import { CustomValidatorMessage } from './validators/custom-validator-message';

/* eslint max-classes-per-file: off */
export abstract class FormControlBase<T> {
  public form: NgForm;
  abstract controlType: string;
  protected defaultValue: T;
  private pendingValue: T;

  // Even though we're not subclassing this into a NodeInputFormControl<T> in order to avoid
  // regression risk and testing effort, having all of these things in here
  // (especially having the overrideAction here instead of injecting SyncAlgorithmsBaseService
  // directly into DynamicFormControlComponent) helps keep it somewhat clean and generic.
  public syncAlgorithmId?: string;
  public overrideAction?: () => Observable<any>;
  public overrideObservable?: Observable<any>;
  public previousValue?: string;
  public overridingInProgress: boolean = false;

  onValueChanged = new Subject();

  options: any;

  get value(): T {
    if (!this.formControl) {
      return this.pendingValue;
    }

    return this.formControl.value;
  }

  set value(value: T) {
    this.setValue(value);
  }

  key: string;
  formControlId: string;
  label: string;
  placeholder: string;
  groupName: string;
  displayLockedAttribute: boolean = false;
  protected _required: boolean;

  get required() {
    return this._required;
  }

  set required(value: boolean) {
    this.setRequired(value);
  }

  order: number;

  protected _disabled: boolean;

  get disabled() {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;

    if (this.formControl) {
      this.updateFormControlState();
    }
  }

  model: any;
  propertyName: string;
  setValueFromModel: boolean = true;

  private _visible: boolean;

  get visible() {
    return this._visible;
  }

  set visible(value: boolean) {
    this._visible = value;

    if (!this.formControl) { return; }

    if (this._visible) {
      this.updateFormControlState();
    } else {
      this.formControl.disable({ emitEvent: false });
    }
  }

  customValidators?: Function[];
  public enableValidation: boolean;
  customValidatorsMessages?: CustomValidatorMessage[];
  showErrorsOnTouched: boolean;
  textOnly: boolean = false;

  formControl: FormControl;
  formGroup: FormGroup;

  constructor(options: FormControlData<T> = {}, form: NgForm = null) {
    this.defaultValue = options.defaultValue;
    this.pendingValue = options.defaultValue;
    this.key = options.key || '';
    this.formControlId = options.formControlId;
    this.label = options.label || '';
    this.placeholder = options.placeholder || '';
    this.setRequired(!!options.required);
    this.order = options.order === undefined ? 1 : options.order;
    this.disabled = !!options.disabled;
    this.options = options.options;
    this.propertyName = options.propertyName;
    this.visible = options.visible === undefined ? true : options.visible;
    this.groupName = options.groupName || '';
    this.customValidators = options.customValidators || [];
    this.enableValidation = options.enableValidation || false;
    this.setValueFromModel = options.setValueFromModel !== false;
    this.customValidatorsMessages = options.customValidatorsMessages || [];
    this.showErrorsOnTouched = options.showErrorsOnTouched || false;
    this.textOnly = options.textOnly || false;
    this.displayLockedAttribute = options.displayLockedAttribute || false;
    this.form = form;
  }

  public setModel(model: any, useDefaultValue: boolean = false) {
    this.model = model;
    this.disabled = this.disabled || (model.isReadOnly != null && model.isReadOnly);

    if (useDefaultValue) {
      this.value = this.defaultValue;
    } else {
      this.initialize();
    }

    if (this.formControl) {
      this.formControl.markAsPristine();
    }
  }

  public commit() {
    _.set(this.model, this.propertyName, this.value);
  }

  setFormControl(formControl: FormControl, group?: any) {
    this.formControl = formControl;

    if (this.pendingValue !== undefined) {
      this.formControl.setValue(this.pendingValue);
    }

    this.setValidators();

    this.formControl.valueChanges.subscribe(value => {
      this.onValueChanged.next(value);
    });
  }

  setValidators() {
    if (!this.formControl) {
      return;
    }

    let validators = [];

    if (this.options && this.options.validators) {
      validators = this.options.validators;
    }

    this.customValidators.forEach(validator => {
      validators.push(validator);
    });

    if (this._required) {
      validators.push(Validators.required);
    }

    this.formControl.setValidators(validators);

    if (!validators.length) {
      this.formControl.clearValidators();
    }

    this.formControl.updateValueAndValidity({ emitEvent: false });
  }

  get isInvalid(): boolean {
    return this.formControl.invalid;
  }

  protected abstract initialize();

  protected initializeDefault() {
    const value = _.get(this.model, this.propertyName) as T;

    let defval = null;

    if (this.options && this.options.defaultValue) {
      defval = this.options.defaultValue;
    }

    this.value = value ? value : defval ? defval : undefined;
  }

  protected setValue(value: T) {
    if (!this.formControl) {
      const isChanged = value !== this.pendingValue;

      this.pendingValue = value;

      if (isChanged) {
        this.onValueChanged.next(value);
      }

      return;
    }

    if (value !== this.formControl.value) {
      this.formControl.setValue(value);
    }
  }

  protected setRequired(value: boolean) {
    this._required = value;
    this.setValidators();
  }

  private updateFormControlState() {
    this._disabled || (this.formGroup && this.formGroup.disabled) || !this._visible
      ? this.formControl.disable({ emitEvent: false })
      : this.formControl.enable({ emitEvent: false });
  }
}

export class HiddenControl<T> extends FormControlBase<T> {
  controlType = 'hidden';

  constructor(options: {} = {}) {
    super(options);
  }

  protected initialize() {
    super.initializeDefault();
  }
}

// TODO: Correct letter case to TextboxControl
export class TextBoxControl extends FormControlBase<string> {
  controlType = 'textbox';

  constructor(options: {} = {}, form: NgForm = null) {
    super(options, form);
  }

  protected initialize() {
    super.initializeDefault();
  }
}

export class PasswordControl extends FormControlBase<string> {
  controlType = 'password';

  constructor(options: {} = {}) {
    super(options);
  }

  protected initialize() {
    super.initializeDefault();
  }
}

// TODO: Correct letter case to TextareaControl
export class TextAreaControl extends FormControlBase<string> {
  controlType = 'textarea';

  constructor(options: {} = {}) {
    super(options);
  }

  protected initialize() {
    super.initializeDefault();
  }
}

export class RichTextControl extends FormControlBase<string> {
  controlType = 'richtext';

  constructor(options: {} = {}) {
    super(options);
  }

  protected initialize() {
    super.initializeDefault();
  }
}

export class CheckBoxControl extends FormControlBase<boolean> {
  controlType = 'checkbox';

  constructor(options: {} = {}) {
    super(options);
    this.value = false;
  }

  protected initialize() {
    const value = _.get(this.model, this.propertyName);

    this.value = value && value.toString().toLowerCase() !== 'false';
  }

  protected setRequired(value: boolean) {
    // Checkboxes should not be required
    this._required = false;
  }
}

export class LabelControl extends FormControlBase<boolean> {
  controlType = 'label';

  constructor(options: {} = {}) {
    super(options);
  }

  protected initialize() {
    super.initializeDefault();
  }
}

export function positiveNumberValidator(control: FormControl) {
  return control.value <= 0 ? { invalidNumber: { value: control.value } } : null;
}

export function positiveWholeNumberValidator(control: FormControl) {
  const number = Math.floor(Number(control.value));
  const valid = number !== Infinity && String(number) === control.value && number >= 0;

  return valid ? null : { invalidNumber: { value: control.value } };
}

export function InListValidator(list: string[]) {
  return (control: FormControl) => list.includes(control.value) ? { inList: { value: control.value } } : null;
}

export class SeparatorControl extends FormControlBase<string> {
  controlType = 'separator';

  constructor(options: {} = {}) {
    super(options);
  }

  protected initialize() {
    super.initializeDefault();
  }
}
